/**
 * Správce hudby a zvuku.
 *
 * Prohlížeče blokují automatické přehrávání zvuku (Autoplay Policy),
 * dokud uživatel neprovede první interakci (klik / stisk klávesy).
 * AudioManager poslouchá první gesto a plynule hudbu prolne (fade-in),
 * takže hraje jak v menu tvorby postavy, tak plynule navazuje do světa.
 */

export class AudioManager {
  private bgm: HTMLAudioElement | null = null;
  private targetVolume = 0.35;
  private fadeInterval: number | null = null;
  private isMuted = false;
  private hasStarted = false;
  private buttonEl: HTMLElement | null = null;
  private sfxContext: AudioContext | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  /** Kdy naposled zaznělo šplouchnutí — brání kulometu při brodění. */
  private lastSplashAt = 0;

  constructor(soundtrackUrl = '/audio/bgm.mp3') {
    // Načtení preference z localStorage
    const savedMuted = localStorage.getItem('hra_music_muted');
    this.isMuted = savedMuted === 'true';

    this.bgm = new Audio(soundtrackUrl);
    this.bgm.loop = true;
    this.bgm.volume = 0;
    this.bgm.preload = 'auto';

    this.createAudioToggleButton();

    // Pokusíme se spustit hned; pokud to prohlížeč zablokuje, počkáme na první klik/klávesu
    this.tryPlay();
    const onFirstUserGesture = () => {
      this.tryPlay();
      window.removeEventListener('pointerdown', onFirstUserGesture);
      window.removeEventListener('keydown', onFirstUserGesture);
    };
    window.addEventListener('pointerdown', onFirstUserGesture, { once: true });
    window.addEventListener('keydown', onFirstUserGesture, { once: true });

    // Klávesa M pro rychlé ztlumení / zapnutí
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyM' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        this.toggleMute();
      }
    });
  }

  private tryPlay(): void {
    if (!this.bgm || this.hasStarted) return;
    this.bgm
      .play()
      .then(() => {
        this.hasStarted = true;
        if (!this.isMuted) {
          this.fadeVolume(this.targetVolume, 2.5);
        }
        this.updateButtonUI();
      })
      .catch(() => {
        // Prohlížeč čeká na interakci
      });
  }

  fadeVolume(target: number, durationSeconds = 1.5): void {
    if (!this.bgm) return;
    if (this.fadeInterval !== null) {
      window.clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }

    const start = this.bgm.volume;
    const diff = target - start;
    const steps = 30;
    const stepTime = (durationSeconds * 1000) / steps;
    let currentStep = 0;

    this.fadeInterval = window.setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      if (this.bgm) {
        this.bgm.volume = Math.max(0, Math.min(1, start + diff * progress));
      }
      if (currentStep >= steps) {
        if (this.fadeInterval !== null) {
          window.clearInterval(this.fadeInterval);
          this.fadeInterval = null;
        }
        if (this.bgm) {
          this.bgm.volume = target;
        }
      }
    }, stepTime);
  }

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    localStorage.setItem('hra_music_muted', String(this.isMuted));

    if (!this.hasStarted) {
      this.tryPlay();
    }

    if (this.isMuted) {
      this.fadeVolume(0, 0.4);
    } else {
      if (this.bgm && this.bgm.paused) {
        this.bgm.play().catch(() => {});
      }
      this.fadeVolume(this.targetVolume, 1.0);
    }

    this.updateButtonUI();
    return this.isMuted;
  }

  setVolume(volume: number): void {
    this.targetVolume = Math.max(0, Math.min(1, volume));
    if (!this.isMuted && this.bgm) {
      this.bgm.volume = this.targetVolume;
    }
  }

  private createAudioToggleButton(): void {
    this.buttonEl = document.createElement('button');
    this.buttonEl.id = 'audio-toggle-btn';
    this.buttonEl.title = 'Hudba (M)';
    this.buttonEl.innerHTML = this.getSpeakerSVG(this.isMuted);

    this.buttonEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMute();
    });

    document.body.appendChild(this.buttonEl);
    this.injectStyles();
    this.updateButtonUI();
  }

  private updateButtonUI(): void {
    if (!this.buttonEl) return;
    this.buttonEl.innerHTML = this.getSpeakerSVG(this.isMuted);
    this.buttonEl.classList.toggle('muted', this.isMuted);
  }

  private getSpeakerSVG(muted: boolean): string {
    if (muted) {
      return `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 5L6 9H2v6h4l5 4V5z"/>
          <line x1="23" y1="9" x2="17" y2="15"/>
          <line x1="17" y1="9" x2="23" y2="15"/>
        </svg>
      `;
    }
    return `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
      </svg>
    `;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      #audio-toggle-btn {
        position: fixed;
        top: 12px;
        right: 12px;
        z-index: 100;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(10, 16, 26, 0.65);
        border: 1px solid rgba(120, 160, 220, 0.2);
        border-radius: 6px;
        color: #b9cde6;
        cursor: pointer;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        transition: all 0.2s ease;
        padding: 0;
      }
      #audio-toggle-btn:hover {
        background: rgba(20, 32, 50, 0.85);
        border-color: rgba(140, 190, 255, 0.45);
        color: #ffffff;
        transform: scale(1.05);
      }
      #audio-toggle-btn:active {
        transform: scale(0.95);
      }
      #audio-toggle-btn.muted {
        color: #7a8a9a;
        opacity: 0.75;
      }
      #audio-toggle-btn svg {
        width: 18px;
        height: 18px;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Šplouchnutí do vody.
   *
   * Zvuk se skládá za běhu, ne z nahrávky. Voda je z hlediska zvuku šum
   * protažený pásmovou propustí, takže se dá udělat přesvědčivě z ničeho —
   * a odpadá tím stahování a hostování dalšího souboru. Navíc jde plynule
   * měnit podle síly dopadu, což u jednoho sampleru nejde.
   *
   * @param strength 0–1, síla dopadu
   */
  playSplash(strength: number): void {
    if (this.isMuted) return;
    const s = Math.min(Math.max(strength, 0), 1);
    if (s < 0.05) return;

    const ctx = this.ensureSfxContext();
    if (!ctx || ctx.state === 'suspended') return;

    // Brodění spouští šplouchnutí často; bez odstupu by se slily v šum.
    const now = ctx.currentTime;
    if (now - this.lastSplashAt < 0.08) return;
    this.lastSplashAt = now;

    const duration = 0.22 + s * 0.5;
    const source = ctx.createBufferSource();
    source.buffer = this.ensureNoiseBuffer(ctx);

    // Pásmová propust klesající s časem dělá ten charakteristický "šplouch":
    // náraz je jasný, dozvuk temný.
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 0.8;
    const startHz = 900 + s * 1700;
    filter.frequency.setValueAtTime(startHz, now);
    filter.frequency.exponentialRampToValueAtTime(Math.max(180, startHz * 0.18), now + duration);

    const gain = ctx.createGain();
    const peak = 0.06 + s * 0.22;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter).connect(gain).connect(ctx.destination);
    source.start(now);
    source.stop(now + duration + 0.05);

    // Silný dopad má navíc dutou ránu — bez ní zní i skok z útesu jako
    // cáknutí dlaní.
    if (s > 0.45) {
      const thump = ctx.createOscillator();
      const thumpGain = ctx.createGain();
      thump.frequency.setValueAtTime(150, now);
      thump.frequency.exponentialRampToValueAtTime(52, now + 0.22);
      thumpGain.gain.setValueAtTime(0.0001, now);
      thumpGain.gain.exponentialRampToValueAtTime(0.10 * s, now + 0.02);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
      thump.connect(thumpGain).connect(ctx.destination);
      thump.start(now);
      thump.stop(now + 0.3);
    }
  }

  private ensureSfxContext(): AudioContext | null {
    if (this.sfxContext) return this.sfxContext;
    try {
      this.sfxContext = new AudioContext();
    } catch {
      // Prohlížeč bez WebAudio prostě bude bez efektů; hra běží dál.
      return null;
    }
    return this.sfxContext;
  }

  /** Vteřina bílého šumu, ze které se krájí každé šplouchnutí. */
  private ensureNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const length = Math.floor(ctx.sampleRate);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buffer;
    return buffer;
  }

  dispose(): void {
    if (this.fadeInterval !== null) {
      window.clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
    if (this.bgm) {
      this.bgm.pause();
      this.bgm.src = '';
      this.bgm = null;
    }
    this.buttonEl?.remove();
  }
}
